import {
  Alert,
  Code,
  ColorInput,
  Divider,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Stack,
  Text,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { WidgetButtonShape, WebBotProviderData } from '@/API';
import { WebBotWidgetIconUpload } from '../WebBotImageUpload';

interface WebBotLayoutTabProps {
  form: UseFormReturnType<WebBotProviderData>;
  workspaceId?: string;
  botProviderId?: string;
}

export function WebBotLayoutTab({ form, workspaceId, botProviderId }: WebBotLayoutTabProps) {
  const layout = form.values.layout;
  const buttonShapeStr = layout?.buttonShape?.toString().toLowerCase();

  return (
    <Stack gap="md">
      <Paper withBorder p="sm" radius="md">
        <Stack gap="sm">
          <Text size="sm" fw={500}>
            視窗相關設定
          </Text>
          <Divider style={{ opacity: 0.6 }} />
          <Group grow>
            <NumberInput
              label="視窗寬度 (px)"
              placeholder="預設 350"
              {...form.getInputProps('layout.windowWidth')}
              value={layout?.windowWidth ?? ''}
            />
            <NumberInput
              label="視窗高度 (px)"
              placeholder="預設 500"
              {...form.getInputProps('layout.windowHeight')}
              value={layout?.windowHeight ?? ''}
            />
          </Group>
          <Group grow align="flex-start">
            <NumberInput
              label="視窗水平微調 X (px)"
              placeholder="正數向右 / 負數向左 (預設 0)"
              {...form.getInputProps('layout.windowOffsetX')}
              value={layout?.windowOffsetX ?? ''}
            />
            <NumberInput
              label="視窗垂直微調 Y (px)"
              placeholder="正數向下 / 負數向上 (預設 0)"
              {...form.getInputProps('layout.windowOffsetY')}
              value={layout?.windowOffsetY ?? ''}
            />
          </Group>
          <Paper withBorder p="sm" radius="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
            <Stack gap="xs">
              <Text size="xs" fw={600} c="dimmed">
                視窗陰影設定 (Window Shadow)
              </Text>
              <ColorInput
                label="陰影顏色"
                placeholder="預設 rgba(0, 0, 0, 0.28) (留空則使用預設)"
                {...form.getInputProps('layout.windowShadowColor')}
                value={layout?.windowShadowColor ?? ''}
              />
              <Group grow align="flex-start">
                <NumberInput
                  label="水平位移 X (px)"
                  placeholder="預設 0"
                  min={-50}
                  max={50}
                  {...form.getInputProps('layout.windowShadowOffsetX')}
                  value={layout?.windowShadowOffsetX ?? 0}
                />
                <NumberInput
                  label="垂直位移 Y (px)"
                  placeholder="預設 3"
                  min={-50}
                  max={50}
                  {...form.getInputProps('layout.windowShadowOffsetY')}
                  value={layout?.windowShadowOffsetY ?? 3}
                />
                <NumberInput
                  label="陰影模糊柔度 Blur (px)"
                  placeholder="預設 8"
                  min={0}
                  max={50}
                  {...form.getInputProps('layout.windowShadowBlur')}
                  value={layout?.windowShadowBlur ?? 8}
                />
              </Group>
            </Stack>
          </Paper>
        </Stack>
      </Paper>

      <Paper withBorder p="sm" radius="md">
        <Stack gap="sm">
          <Text size="sm" fw={500}>
            開關按鈕相關設定
          </Text>
          <Divider style={{ opacity: 0.6 }} />
          <Stack gap="xs">
            <Text size="xs" c="dimmed">
              開關按鈕樣式 / 顯示狀態
            </Text>
            <SegmentedControl
              data={[
                { label: '圓形剪裁', value: WidgetButtonShape.Circle },
                { label: '原始形狀', value: WidgetButtonShape.Original },
                { label: '隱藏按鈕 (常駐/API模式)', value: WidgetButtonShape.Hide },
              ]}
              value={
                buttonShapeStr === 'hide'
                  ? WidgetButtonShape.Hide
                  : buttonShapeStr === 'original'
                  ? WidgetButtonShape.Original
                  : WidgetButtonShape.Circle
              }
              onChange={(val) => form.setFieldValue('layout.buttonShape', val as WidgetButtonShape)}
            />
          </Stack>

          {buttonShapeStr === 'hide' ? (
            <Alert color="blue" variant="light">
              開關按鈕已隱藏。使用者可點擊「常駐模式」的擴充元件，或由網頁調用 JavaScript API (<Code>widget.open()</Code>) 來開啟聊天視窗。
            </Alert>
          ) : (
            <>
              {workspaceId && botProviderId && (
                <WebBotWidgetIconUpload
                  workspaceId={workspaceId}
                  botProviderId={botProviderId}
                  shape={layout?.buttonShape}
                />
              )}
              <Group grow>
                <NumberInput
                  label="開關按鈕寬度 (px)"
                  placeholder="預設 60"
                  min={20}
                  max={300}
                  {...form.getInputProps('layout.buttonWidth')}
                  value={layout?.buttonWidth ?? ''}
                />
                <NumberInput
                  label="開關按鈕高度 (px)"
                  placeholder="預設 60"
                  min={20}
                  max={300}
                  {...form.getInputProps('layout.buttonHeight')}
                  value={layout?.buttonHeight ?? ''}
                />
              </Group>
            </>
          )}
          <Group grow align="flex-start">
            <NumberInput
              label="開關按鈕左側邊距 (px)"
              placeholder="未設定 (若與右側皆填寫則置中)"
              {...form.getInputProps('layout.positionLeft')}
              value={layout?.positionLeft ?? ''}
            />
            <NumberInput
              label="開關按鈕右側邊距 (px)"
              placeholder="預設 20 (若與左側皆填寫則置中)"
              {...form.getInputProps('layout.positionRight')}
              value={layout?.positionRight ?? ''}
            />
          </Group>
          <Group grow align="flex-start">
            <NumberInput
              label="開關按鈕頂部邊距 (px)"
              placeholder="未設定 (若與底部皆填寫則置中)"
              {...form.getInputProps('layout.positionTop')}
              value={layout?.positionTop ?? ''}
            />
            <NumberInput
              label="開關按鈕底部邊距 (px)"
              placeholder="預設 20 (若與頂部皆填寫則置中)"
              {...form.getInputProps('layout.positionBottom')}
              value={layout?.positionBottom ?? ''}
            />
          </Group>
          {buttonShapeStr !== 'hide' && (
            <Paper withBorder p="sm" radius="sm" style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
              <Stack gap="xs">
                <Text size="xs" fw={600} c="dimmed">
                  開關按鈕陰影設定 (Button Shadow)
                </Text>
                <ColorInput
                  label="陰影顏色"
                  placeholder="預設 rgba(0, 0, 0, 0.28) (留空則使用預設)"
                  {...form.getInputProps('layout.buttonShadowColor')}
                  value={layout?.buttonShadowColor ?? ''}
                />
                <Group grow align="flex-start">
                  <NumberInput
                    label="水平位移 X (px)"
                    placeholder="預設 0"
                    min={-50}
                    max={50}
                    {...form.getInputProps('layout.buttonShadowOffsetX')}
                    value={layout?.buttonShadowOffsetX ?? 0}
                  />
                  <NumberInput
                    label="垂直位移 Y (px)"
                    placeholder="預設 3"
                    min={-50}
                    max={50}
                    {...form.getInputProps('layout.buttonShadowOffsetY')}
                    value={layout?.buttonShadowOffsetY ?? 3}
                  />
                  <NumberInput
                    label="陰影模糊柔度 Blur (px)"
                    placeholder="預設 8"
                    min={0}
                    max={50}
                    {...form.getInputProps('layout.buttonShadowBlur')}
                    value={layout?.buttonShadowBlur ?? 8}
                  />
                </Group>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
